import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Box, Button, IconButton, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import ApartmentIcon from "@mui/icons-material/Apartment";
import DnsIcon from "@mui/icons-material/Dns";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import MemoryIcon from "@mui/icons-material/Memory";
import WifiIcon from "@mui/icons-material/Wifi";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import RuleIcon from "@mui/icons-material/Rule";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import dayjs from "dayjs";
import { colors } from "../../../../theme";
import API from "../../../../api";
import { mapAssetRows, severityStyleForTier } from "../../status/status-components/assetUtils";
import { computeZoneSecurityScore } from "../../status/status-components/securityScore";
import { zoneRiskLabel, zoneRiskStyle } from "../../status/status-components/zoneUtils";
import ConduitRuleManagerDialog from "../../status/status-components/ConduitRuleManagerDialog";
import ZoneFormDialog from "./ZoneFormDialog";
import WarningDialog from "../../../../components/WarningDialog";

// Diagramma "Purdue model" (IEC 62443 / ISA-95) — TASK-AI-031, terza
// revisione su feedback dell'utente: le prime due (tabelle, poi un grafo
// libero con le linee sovrapposte alle card) non andavano bene. Questa
// versione riprende un mockup validato (vedi task PROGRESS.md) con:
//  - una corsia laterale con icona per livello Purdue;
//  - i conduit che saltano più di un livello ("flyover") instradati in un
//    corridoio a destra del canvas riservato via layout (non stimato a
//    posteriori) — così NON possono mai attraversare una card, qualunque
//    sia la larghezza dello schermo;
//  - selezione di una zona → pannello "inspector" a destra con dettaglio
//    SL/rischio/conduit, invece di ammassare tutto sulle card o nei tooltip;
//  - una seconda vista "Matrix" (zona × zona) per l'audit veloce.
//
// Le zone sono le stesse della feature "Zones" manuale esistente
// (name/note/risk, assegnate per asset via zone_id) -- non una lista
// parallela. `risk` (sempre disponibile) e target/achieved Security Level
// (disponibili solo se Orion ha visibilità su quella zona, stesso nome
// case-insensitive) sono due assi diversi che convivono sulla stessa zona:
// è quello che rende la pagina utile anche in una rete solo IT. Le zone che
// Orion vede ma senza una zona manuale omonima restano visibili come
// "auto-detected" invece di essere perse.
const TIERS = [
  { level: 5, label: "Enterprise Network", Icon: ApartmentIcon },
  { level: 4, label: "Site Business / Logistics", Icon: DnsIcon },
  { level: 3, label: "Site Operations", Icon: DnsIcon },
  { level: 2, label: "Area Supervisory Control", Icon: PrecisionManufacturingIcon },
  { level: 1, label: "Basic Control", Icon: PrecisionManufacturingIcon },
  { level: 0, label: "Physical Process", Icon: MemoryIcon },
];
const UNCLASSIFIED_INDEX = TIERS.length; // sorts after every real Purdue level

const ALLOWED_COLOR = "#4cceac";
const DENIED_COLOR = "#ff4444";
// Un conduit "misto" (una regola allowed A→B + una not-allowed B→A, entrambe
// legittime -- vedi ConduitRuleManagerDialog) non è né "tutto ok" né "tutto
// bloccato": mostrarlo in rosso pieno (il vecchio "peggior caso") lo fa
// leggere come un problema quando in realtà è una policy direzionale voluta.
const MIXED_COLOR = "#ffb422";
const GUTTER = 108; // px riservati a destra del canvas per i conduit "flyover"

const slPct = (v) => Math.max(0, Math.min(100, (v / 4) * 100));

// ---------------------------------------------------------------------------
const ZoneNode = ({ zone, selected, dimmed, cardRef, onClick }) => {
  const hasRisk = zone.risk !== null && zone.risk !== undefined;
  const hasTarget = zone.target_sl !== null && zone.target_sl !== undefined;
  const hasAchieved = zone.achieved_sl !== null && zone.achieved_sl !== undefined;
  const riskStyle = hasRisk ? zoneRiskStyle(zone.risk) : null;
  const gapStyle = hasAchieved ? severityStyleForTier(zone.gap > 0 ? "critical" : "verylow") : null;
  const borderColor = riskStyle?.color ?? colors.grey[700];
  const levelEstimated = zone.purdue_level_source === "estimated";
  const Icon = zone.tierIcon;

  return (
    <Box
      ref={cardRef}
      onClick={() => onClick(zone.zone)}
      sx={{
        position: "relative", width: 208, p: 1.1, borderRadius: 1.5,
        backgroundColor: selected ? "#262c38" : "#1c1c1c",
        border: `1px ${levelEstimated ? "dashed" : "solid"} ${selected ? "#459095" : colors.grey[800]}`,
        boxShadow: selected ? "0 0 0 1px #459095" : "none",
        borderLeft: `3px solid ${borderColor}`,
        display: "flex", flexDirection: "column", gap: 0.5, cursor: "pointer",
        opacity: dimmed ? 0.35 : 1,
        transition: "opacity .12s, border-color .12s, background-color .12s",
        "&:hover": { borderColor: "#459095" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
        <Box sx={{ width: 18, height: 18, borderRadius: 0.8, bgcolor: "#151515", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Icon sx={{ fontSize: 12, color: colors.grey[500] }} />
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: 12.5, color: colors.grey[100], flex: 1, minWidth: 0 }} noWrap>
          {zone.zone}
        </Typography>
        {zone.unauthorized_conduit_count > 0 && (
          <WarningAmberIcon sx={{ fontSize: 15, color: DENIED_COLOR, flexShrink: 0 }} titleAccess="Unauthorized conduits" />
        )}
      </Box>
      <Box sx={{ display: "flex", gap: 0.6, flexWrap: "wrap" }}>
        {hasRisk && (
          <Box sx={{ px: 0.8, py: 0.15, borderRadius: 1, fontSize: 10, fontWeight: 700, bgcolor: riskStyle.bg, color: riskStyle.color }}>
            {zoneRiskLabel(zone.risk)}
          </Box>
        )}
        {zone.securityScore && (
          <Box sx={{ px: 0.8, py: 0.15, borderRadius: 1, fontSize: 10, fontWeight: 700, bgcolor: `${zone.securityScore.color}22`, color: zone.securityScore.color }}>
            Score {zone.securityScore.score}
          </Box>
        )}
        {hasAchieved ? (
          <Box sx={{ px: 0.8, py: 0.15, borderRadius: 1, fontSize: 10, fontWeight: 700, bgcolor: gapStyle.bg, color: gapStyle.color }}>
            SL {zone.achieved_sl}/{zone.target_sl}
          </Box>
        ) : hasTarget ? (
          <Box sx={{ px: 0.8, py: 0.15, borderRadius: 1, fontSize: 10, fontWeight: 700, bgcolor: `${colors.grey[700]}55`, color: colors.grey[300] }}>
            Target SL {zone.target_sl}
          </Box>
        ) : (
          <Box sx={{ px: 0.8, py: 0.15, borderRadius: 1, fontSize: 10, fontWeight: 600, bgcolor: "#ffffff0f", color: colors.grey[500] }}>
            No SL data
          </Box>
        )}
        {zone.source === "orion" && (
          <Box sx={{ px: 0.8, py: 0.15, borderRadius: 1, fontSize: 10, fontWeight: 600, bgcolor: `${colors.grey[700]}55`, color: colors.grey[400] }}>
            auto-detected
          </Box>
        )}
      </Box>
      <Typography sx={{ fontSize: 10.5, color: colors.grey[500] }}>
        {zone.asset_count} asset{zone.asset_count === 1 ? "" : "s"}
        {zone.ot_asset_count > 0 ? ` · ${zone.ot_asset_count} OT` : ""}
      </Typography>
      {levelEstimated && (
        <Typography sx={{ fontSize: 9.5, color: colors.grey[600], fontStyle: "italic" }}>
          Purdue level estimated — not yet confirmed by Orion
        </Typography>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
const Inspector = ({ zone, edges, onEdit, onDelete }) => {
  const [expanded, setExpanded] = useState(() => new Set());

  if (!zone) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: colors.grey[600], letterSpacing: 0.6, textTransform: "uppercase" }}>
          Inspector
        </Typography>
        <Typography sx={{ fontSize: 12, color: colors.grey[600], fontStyle: "italic", mt: 1 }}>
          Click a zone in the diagram to see its Security Level detail and conduits here.
        </Typography>
      </Box>
    );
  }

  const hasRisk = zone.risk !== null && zone.risk !== undefined;
  const hasAchieved = zone.achieved_sl !== null && zone.achieved_sl !== undefined;
  const riskStyle = hasRisk ? zoneRiskStyle(zone.risk) : null;
  const related = edges.filter((e) => e.zone_orig === zone.zone || e.zone_resp === zone.zone);

  const toggleExpanded = (peer) => setExpanded((cur) => {
    const next = new Set(cur);
    if (next.has(peer)) next.delete(peer); else next.add(peer);
    return next;
  });

  return (
    <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
        <Box>
          <Typography sx={{ fontSize: 10, fontWeight: 700, color: colors.grey[600], letterSpacing: 0.6, textTransform: "uppercase" }}>
            Selected zone
          </Typography>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: colors.grey[100], mt: 0.3 }}>{zone.zone}</Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 0.3, flexShrink: 0 }}>
          <Tooltip title="Edit zone">
            <IconButton size="small" onClick={() => onEdit(zone.zone)} sx={{ color: colors.grey[500], p: 0.5, "&:hover": { color: "#459095" } }}>
              <EditIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete zone">
            <IconButton size="small" onClick={() => onDelete(zone.zone)} sx={{ color: colors.grey[500], p: 0.5, "&:hover": { color: DENIED_COLOR } }}>
              <DeleteIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box>
        {[
          ["Risk", hasRisk ? zoneRiskLabel(zone.risk) : "Not set", hasRisk ? riskStyle.color : colors.grey[500]],
          ["Security score", zone.securityScore ? zone.securityScore.score : "—", zone.securityScore ? zone.securityScore.color : colors.grey[500]],
          ["Assets", zone.asset_count, colors.grey[100]],
          ["OT assets", zone.ot_asset_count, colors.grey[100]],
        ].map(([k, v, c]) => (
          <Box key={k} sx={{ display: "flex", justifyContent: "space-between", py: 0.7, borderBottom: `1px solid ${colors.grey[800]}` }}>
            <Typography sx={{ fontSize: 11, color: colors.grey[500] }}>{k}</Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: c }}>{v}</Typography>
          </Box>
        ))}
      </Box>

      <Box>
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: colors.grey[600], letterSpacing: 0.6, textTransform: "uppercase", mb: 0.8 }}>
          Security level
        </Typography>
        {hasAchieved ? (
          <Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: colors.grey[500], mb: 0.4 }}>
              <span>Achieved SL {zone.achieved_sl}</span><span>Target SL {zone.target_sl}</span>
            </Box>
            <Box sx={{ position: "relative", height: 6, borderRadius: 1, bgcolor: colors.grey[800] }}>
              <Box sx={{
                position: "absolute", top: 0, left: 0, height: "100%", borderRadius: 1,
                width: `${slPct(zone.achieved_sl)}%`,
                bgcolor: zone.achieved_sl < zone.target_sl ? DENIED_COLOR : ALLOWED_COLOR,
              }} />
              <Box sx={{ position: "absolute", top: -3, width: 2, height: 12, bgcolor: colors.grey[200], left: `${slPct(zone.target_sl)}%` }} />
            </Box>
          </Box>
        ) : zone.target_sl !== null && zone.target_sl !== undefined ? (
          <Typography sx={{ fontSize: 11.5, color: colors.grey[500], fontStyle: "italic" }}>
            Target SL {zone.target_sl} set manually — no Orion visibility yet to compute an achieved SL.
          </Typography>
        ) : (
          <Typography sx={{ fontSize: 11.5, color: colors.grey[600], fontStyle: "italic" }}>
            No Orion visibility on this zone yet.
          </Typography>
        )}
      </Box>

      {zone.unauthorized_conduit_count > 0 && (
        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", bgcolor: "#ff222218", border: `1px solid ${DENIED_COLOR}`, borderRadius: 1.5, p: 1 }}>
          <WarningAmberIcon sx={{ fontSize: 16, color: DENIED_COLOR, mt: "1px", flexShrink: 0 }} />
          <Typography sx={{ fontSize: 11.5, color: colors.grey[100] }}>
            {zone.unauthorized_conduit_count} conduit{zone.unauthorized_conduit_count === 1 ? "" : "s"} reaching this zone {zone.unauthorized_conduit_count === 1 ? "is" : "are"} not on the allow-list.
          </Typography>
        </Box>
      )}

      {zone.note && (
        <Typography sx={{ fontSize: 11.5, color: colors.grey[500] }}>{zone.note}</Typography>
      )}

      <Box>
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: colors.grey[600], letterSpacing: 0.6, textTransform: "uppercase", mb: 0.8 }}>
          Conduits ({related.length} zone{related.length === 1 ? "" : "s"})
        </Typography>
        {related.length === 0 ? (
          <Typography sx={{ fontSize: 11.5, color: colors.grey[600], fontStyle: "italic" }}>None observed or declared.</Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {related.map((e) => {
              const outbound = e.zone_orig === zone.zone;
              const peer = outbound ? e.zone_resp : e.zone_orig;
              const dotColor = e.allowed ? ALLOWED_COLOR : DENIED_COLOR;
              const isOpen = expanded.has(peer);
              return (
                <Box key={peer}>
                  <Box
                    onClick={() => toggleExpanded(peer)}
                    sx={{ display: "flex", alignItems: "center", gap: 0.8, fontSize: 11.5, cursor: "pointer", py: 0.2, "&:hover": { opacity: 0.8 } }}
                  >
                    <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: dotColor, flexShrink: 0, opacity: e.declared ? 0.5 : 1, border: e.declared ? `1px dashed ${dotColor}` : "none" }} />
                    <Typography sx={{ fontSize: 11, color: colors.grey[600], flexShrink: 0 }}>{outbound ? "→" : "←"}</Typography>
                    <Typography sx={{ fontSize: 11.5, color: colors.grey[100], flex: 1, minWidth: 0 }} noWrap>{peer}</Typography>
                    <Typography sx={{ fontSize: 10.5, color: colors.grey[600], flexShrink: 0 }}>
                      {e.count} conduit{e.count === 1 ? "" : "s"} {isOpen ? "▾" : "▸"}
                    </Typography>
                  </Box>
                  {isOpen && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4, pl: 2, pt: 0.4, pb: 0.4 }}>
                      {e.members.map((m, i) => (
                        <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 0.8, fontSize: 10.5 }}>
                          <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: m.allowed ? ALLOWED_COLOR : DENIED_COLOR, flexShrink: 0, opacity: m.source === "declared" ? 0.5 : 1, border: m.source === "declared" ? `1px dashed ${m.allowed ? ALLOWED_COLOR : DENIED_COLOR}` : "none" }} />
                          <Typography sx={{ fontSize: 10.5, color: colors.grey[300], flex: 1, minWidth: 0 }} noWrap>
                            {m.service || "any service"}
                            {m.source === "declared" && m.direction && m.direction !== "both" && (
                              <Typography component="span" sx={{ fontSize: 9.5, color: colors.grey[600], ml: 0.5 }}>
                                ({m.direction === "a_to_b" ? `${m.zone_orig} → ${m.zone_resp}` : `${m.zone_resp} → ${m.zone_orig}`})
                              </Typography>
                            )}
                          </Typography>
                          <Typography sx={{ fontSize: 10, color: colors.grey[600], flexShrink: 0 }}>
                            {m.allowed ? "allowed" : "not allowed"}{m.source === "declared" ? " · planned" : ""}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
const MatrixView = ({ zoneStats, conduits, onSelectZone, onSelectPair }) => {
  const names = zoneStats.map((z) => z.zone);
  // Un pair può avere più conduit (servizi diversi) -- si accumula in un
  // array invece di sovrascrivere, altrimenti la cella perderebbe tutti i
  // record tranne l'ultimo. La cella mostra un aggregato (peggior caso),
  // il pannello di dettaglio a destra (click sulla cella) mostra la lista
  // completa.
  // Una regola DICHIARATA può essere a senso unico (direction 'a_to_b' o
  // 'b_to_a', vedi ConduitRuleManagerDialog): due regole opposte per la
  // stessa coppia (una allowed A→B, una not-allowed B→A) sono legittime e
  // NON in conflitto. Senza rispettare `direction` qui, entrambe finivano in
  // entrambe le celle e il "peggior caso" marcava rosso anche il verso
  // effettivamente allowed. `direction` assente (dato osservato/"actual")
  // equivale a 'both': il traffico osservato resta simmetrico come prima.
  const edgeMap = new Map();
  conduits.forEach((c) => {
    if (!c.zone_orig || !c.zone_resp) return;
    const dir = c.direction || 'both';
    const key1 = `${c.zone_orig}|${c.zone_resp}`;
    const key2 = `${c.zone_resp}|${c.zone_orig}`;
    if (dir === 'both' || dir === 'a_to_b') {
      if (!edgeMap.has(key1)) edgeMap.set(key1, []);
      edgeMap.get(key1).push(c);
    }
    if (dir === 'both' || dir === 'b_to_a') {
      if (!edgeMap.has(key2)) edgeMap.set(key2, []);
      edgeMap.get(key2).push(c);
    }
  });

  return (
    <Box sx={{ overflowX: "auto" }}>
      <Box sx={{ display: "grid", gridTemplateColumns: `160px repeat(${names.length}, 46px)`, gap: "5px", alignItems: "end" }}>
        <Box />
        {names.map((n) => (
          <Box
            key={n}
            onClick={() => onSelectZone(n)}
            sx={{
              writingMode: "vertical-rl", transform: "rotate(180deg)", height: 130,
              fontSize: 11, color: colors.grey[400], fontWeight: 600, textAlign: "right", pb: 1, whiteSpace: "nowrap",
              cursor: "pointer", "&:hover": { color: "#459095" },
            }}
          >
            {n}
          </Box>
        ))}
      </Box>
      {names.map((rowName) => (
        <Box key={rowName} sx={{ display: "grid", gridTemplateColumns: `160px repeat(${names.length}, 46px)`, gap: "5px", mt: "5px" }}>
          <Typography
            onClick={() => onSelectZone(rowName)}
            sx={{ fontSize: 11.5, color: colors.grey[300], fontWeight: 600, alignSelf: "center", cursor: "pointer", "&:hover": { color: "#459095" } }}
          >
            {rowName}
          </Typography>
          {names.map((colName) => {
            if (rowName === colName) return <Box key={colName} sx={{ width: 44, height: 44, borderRadius: 1.2, bgcolor: colors.grey[800] }} />;
            const members = edgeMap.get(`${rowName}|${colName}`) || [];
            let bg = "#1c1c1c", border = colors.grey[800], mark = null, markColor = null, dash = "solid";
            if (members.length > 0) {
              const good = members.every((m) => !!m.allowed);
              bg = good ? "#4cceac1c" : "#ff44441c";
              border = good ? ALLOWED_COLOR : DENIED_COLOR;
              markColor = good ? ALLOWED_COLOR : DENIED_COLOR;
              mark = good ? "✓" : "✕";
              dash = members.every((m) => m.source === "declared") ? "dashed" : "solid";
            }
            const title = members.length > 0
              ? `${rowName} ⇄ ${colName} · ${members.length} conduit${members.length === 1 ? "" : "s"}`
              : "No data either way";
            return (
              <Box
                key={colName}
                title={title}
                onClick={() => onSelectPair(rowName, colName)}
                sx={{
                  width: 44, height: 44, borderRadius: 1.2, bgcolor: bg,
                  border: `1.5px ${dash} ${border}`, cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  "&:hover": { boxShadow: "0 0 0 1px #459095" },
                }}
              >
                {mark && <Typography sx={{ fontSize: 15, fontWeight: 700, color: markColor }}>{mark}</Typography>}
              </Box>
            );
          })}
        </Box>
      ))}
      <Box sx={{ display: "flex", gap: 2.5, flexWrap: "wrap", mt: 2.5 }}>
        {[
          ["#4cceac1c", ALLOWED_COLOR, "solid", "Observed · allowed"],
          ["#ff44441c", DENIED_COLOR, "solid", "Observed · not allowed"],
          ["transparent", ALLOWED_COLOR, "dashed", "Declared · allowed"],
          ["transparent", DENIED_COLOR, "dashed", "Declared · not allowed"],
          ["#1c1c1c", colors.grey[800], "solid", "No data either way"],
        ].map(([bg, border, dash, label]) => (
          <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
            <Box sx={{ width: 15, height: 15, borderRadius: 0.8, bgcolor: bg, border: `1.5px ${dash} ${border}` }} />
            <Typography sx={{ fontSize: 11, color: colors.grey[400] }}>{label}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Dettaglio di UNA coppia di zone (click su una cella della Matrix, non
// un'intestazione riga/colonna -- quella popola l'Inspector normale, stesso
// pannello della topology view). Mutuamente esclusivo con Inspector: un solo
// pannello alla volta sulla destra, vedi `selected`/`selectedPair` in
// NetworkZones.
const PairInspector = ({ pair, conduits, onOpenRules }) => {
  const members = conduits.filter((c) =>
    (c.zone_orig === pair.zoneA && c.zone_resp === pair.zoneB) ||
    (c.zone_orig === pair.zoneB && c.zone_resp === pair.zoneA)
  );

  return (
    <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
      <Box>
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: colors.grey[600], letterSpacing: 0.6, textTransform: "uppercase" }}>
          Zone pair
        </Typography>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: colors.grey[100], mt: 0.3 }}>
          {pair.zoneA} ⇄ {pair.zoneB}
        </Typography>
      </Box>

      <Box>
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: colors.grey[600], letterSpacing: 0.6, textTransform: "uppercase", mb: 0.8 }}>
          Conduits ({members.length})
        </Typography>
        {members.length === 0 ? (
          <Typography sx={{ fontSize: 11.5, color: colors.grey[600], fontStyle: "italic" }}>No data either way.</Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.7 }}>
            {members.map((m, i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 0.8, fontSize: 11.5 }}>
                <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: m.allowed ? ALLOWED_COLOR : DENIED_COLOR, flexShrink: 0, opacity: m.source === "declared" ? 0.5 : 1, border: m.source === "declared" ? `1px dashed ${m.allowed ? ALLOWED_COLOR : DENIED_COLOR}` : "none" }} />
                <Typography sx={{ fontSize: 11.5, color: colors.grey[100], flex: 1, minWidth: 0 }} noWrap>
                  {m.service || "any service"}
                  {/* Regole dichiarate a senso unico ("direction" != both): senza
                      questo indicatore due regole opposte per la stessa coppia
                      (una allowed A→B, una not-allowed B→A) sembrano una
                      contraddizione invece di due policy direzionali distinte. */}
                  {m.source === "declared" && m.direction && m.direction !== "both" && (
                    <Typography component="span" sx={{ fontSize: 10, color: colors.grey[600], ml: 0.6 }}>
                      ({m.direction === "a_to_b" ? `${m.zone_orig} → ${m.zone_resp}` : `${m.zone_resp} → ${m.zone_orig}`})
                    </Typography>
                  )}
                </Typography>
                <Typography sx={{ fontSize: 10.5, color: colors.grey[600], flexShrink: 0 }}>
                  {m.allowed ? "allowed" : "not allowed"}{m.source === "declared" ? " · planned" : ""}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <Button size="small" variant="outlined" onClick={onOpenRules} sx={{ textTransform: "none", fontSize: 12 }}>
        Edit in Manage conduit rules
      </Button>
    </Box>
  );
};

// ---------------------------------------------------------------------------
const NetworkZones = () => {
  const [zoneStats, setZoneStats] = useState([]);
  const [pushMeta, setPushMeta] = useState({ pendingPush: false, lastPushedAt: null });
  const [conduits, setConduits] = useState([]);
  const [rules, setRules] = useState([]);
  const [manualZones, setManualZones] = useState([]);
  // Asset rows + CVE acks -- only fetched to compute each zone's security score
  // client-side (computeZoneSecurityScore), same inputs the Network Overview
  // page already fetches for the same purpose.
  const [assetRows, setAssetRows] = useState([]);
  const [acks, setAcks] = useState([]);
  const [rulesDialogOpen, setRulesDialogOpen] = useState(false);
  // Create/edit di UNA zona alla volta -- aperto dal "+" per riga/livello
  // Purdue (create, zone=null) o dal pannello Inspector di una zona
  // selezionata (edit, zone=oggetto). Sostituisce il vecchio dialog "elenca
  // tutte le zone", ora ridondante: il grafo stesso mostra ogni zona.
  const [formDialog, setFormDialog] = useState({ open: false, zone: null, initialPurdueLevel: null });
  const [deleteZoneDialog, setDeleteZoneDialog] = useState({ open: false, zone: null });
  const [pushState, setPushState] = useState({ status: "idle", message: "" }); // idle | pushing | done | error
  // Errore dell'ultima azione CRUD su zone/regole (create/update/delete) --
  // prima veniva solo loggato in console.error, quindi un fallimento (es.
  // validazione lato server, conflitto) sembrava "non fare nulla" agli occhi
  // dell'utente. Mostrato come banner finché non si ritenta con successo o
  // non lo si chiude a mano.
  const [actionError, setActionError] = useState("");
  const [view, setView] = useState("topology");
  const [selected, setSelected] = useState(null);
  // Coppia di zone selezionata cliccando una CELLA della Matrix view (non
  // un'intestazione riga/colonna, quella popola `selected` come nella
  // topology view) -- i due stati sono mutuamente esclusivi, un solo
  // pannello di dettaglio alla volta sulla destra.
  const [selectedPair, setSelectedPair] = useState(null);
  const [edges, setEdges] = useState([]);
  const [size, setSize] = useState({ width: 0, height: 0 });

  const containerRef = useRef(null);
  const cardNodes = useRef({});

  const loadZoneStats = useCallback((signal) => {
    API.getZoneStats({}, signal)
      .then((result) => {
        setZoneStats(result.data.data || []);
        setPushMeta(result.data.meta || { pendingPush: false, lastPushedAt: null });
      })
      .catch((error) => console.error("[NetworkZones] Error fetching zone stats:", error));
  }, []);

  const loadConduits = useCallback((signal) => {
    API.getZoneConduits({}, signal)
      .then((result) => setConduits(result.data.data || []))
      .catch((error) => console.error("[NetworkZones] Error fetching zone conduits:", error));
  }, []);

  const loadRules = useCallback(() => {
    API.getConduitRules()
      .then((result) => setRules(result.data.data || []))
      .catch((error) => console.error("[NetworkZones] Error fetching conduit rules:", error));
  }, []);

  // Zone "grezze" (documenti Zone reali, non la vista arricchita
  // zoneStats) -- serve a ZoneManagerDialog, che qui vive senza selezione
  // di asset (definizione zona pura, non assegnazione): la sezione
  // "assign to N selected assets" del dialog resta nascosta (selectedCount=0).
  const loadManualZones = useCallback(() => {
    API.getZones()
      .then((result) => { if (result.data?.success) setManualZones(result.data.data || []); })
      .catch((error) => console.error("[NetworkZones] Error fetching zones:", error));
  }, []);

  const loadAssetRows = useCallback((signal) => {
    API.getRecentHost({ serials: JSON.stringify([]), all: true, orion: true }, signal)
      .then((result) => {
        const data = result.data || result;
        if (data?.success) setAssetRows(mapAssetRows(data.data));
      })
      .catch((error) => console.error("[NetworkZones] Error fetching asset rows:", error));
  }, []);

  const loadAcks = useCallback(() => {
    API.getCveAcks()
      .then((result) => { if (result.data?.success) setAcks(result.data.data || []); })
      .catch((error) => console.error("[NetworkZones] Error fetching CVE acks:", error));
  }, []);

  useEffect(() => {
    const abortController = new AbortController();
    const { signal } = abortController;
    loadZoneStats(signal);
    loadConduits(signal);
    loadRules();
    loadManualZones();
    loadAssetRows(signal);
    loadAcks();
    return () => abortController.abort();
  }, [loadZoneStats, loadConduits, loadRules, loadManualZones, loadAssetRows, loadAcks]);

  // Same formula as the network-wide score and the Network Overview page
  // (computeZoneSecurityScore reuses computeSecurityScore, scoped to this zone's
  // own assets) -- attached to the enriched zoneStats view so every consumer below
  // (cards, Inspector, Purdue-tier grouping) sees it without a separate lookup.
  // Sorted riskiest-first (lowest score = most at risk) so each Purdue tier lists
  // its worst zone first; zones with nothing to score yet (no assets) sort last.
  const zoneStatsWithScore = useMemo(
    () => zoneStats
      .map((z) => ({ ...z, securityScore: computeZoneSecurityScore(z.zone_id, { rows: assetRows, zones: manualZones, acks }) }))
      .sort((a, b) => (a.securityScore?.score ?? 101) - (b.securityScore?.score ?? 101)),
    [zoneStats, assetRows, manualZones, acks]
  );

  const handleCreateRule = (payload) => {
    API.createConduitRule(payload)
      .then((res) => {
        if (res.data.success) { setActionError(""); loadRules(); loadConduits(); loadZoneStats(); }
        else setActionError(res.data.error || "Failed to create conduit rule");
      })
      .catch((err) => {
        console.error("[handleCreateRule] Error:", err);
        setActionError(err?.response?.data?.error || "Failed to create conduit rule");
      });
  };
  const handleUpdateRule = (id, payload) => {
    API.updateConduitRule(id, payload)
      .then((res) => {
        if (res.data.success) { setActionError(""); loadRules(); loadConduits(); loadZoneStats(); }
        else setActionError(res.data.error || "Failed to update conduit rule");
      })
      .catch((err) => {
        console.error("[handleUpdateRule] Error:", err);
        setActionError(err?.response?.data?.error || "Failed to update conduit rule");
      });
  };
  const handleDeleteRule = (id) => {
    API.deleteConduitRule(id)
      .then((res) => {
        if (res.data.success) { setActionError(""); loadRules(); loadConduits(); loadZoneStats(); }
        else setActionError(res.data.error || "Failed to delete conduit rule");
      })
      .catch((err) => {
        console.error("[handleDeleteRule] Error:", err);
        setActionError(err?.response?.data?.error || "Failed to delete conduit rule");
      });
  };

  // Definizione zona (name/note/risk/target SL/Purdue level/subnet/VLAN) --
  // stesso dialog usato altrove (Overview/Inventory) per l'assegnazione
  // massiva agli asset, riusato qui in modalità "solo definizione" perché
  // questa pagina non ha una selezione di asset. È l'unico posto dove
  // creare/modificare le zone in modo completo: subnet/VLAN/target SL
  // servono solo qui (per il push a Orion), quindi ha senso che la
  // definizione viva dove vive anche il bottone "Push to Orion".
  const handleCreateZone = (payload) => {
    API.createZone(payload)
      .then((res) => {
        if (res.data.success) { setActionError(""); loadManualZones(); loadZoneStats(); }
        else setActionError(res.data.error || "Failed to create zone");
      })
      .catch((err) => {
        console.error("[handleCreateZone] Error:", err);
        setActionError(err?.response?.data?.error || "Failed to create zone");
      });
  };
  const handleUpdateZone = (id, payload) => {
    API.updateZone(id, payload)
      // Renaming a zone cascades server-side onto every conduit rule declared
      // against its old name (see zones-ctrl.mjs updateZone) -- reload rules/
      // conduits too, not just the zone list, or the dialog/matrix keep
      // showing the pre-rename name until the page is revisited.
      .then((res) => {
        if (res.data.success) { setActionError(""); loadManualZones(); loadZoneStats(); loadRules(); loadConduits(); }
        else setActionError(res.data.error || "Failed to update zone");
      })
      .catch((err) => {
        console.error("[handleUpdateZone] Error:", err);
        setActionError(err?.response?.data?.error || "Failed to update zone");
      });
  };
  const handleDeleteZone = (id) => {
    API.deleteZone(id)
      // Deleting a zone drops any conduit rule that referenced it server-side
      // (see zones-ctrl.mjs deleteZone) -- same reasoning as the rename above.
      .then((res) => {
        if (res.data.success) { setActionError(""); loadManualZones(); loadZoneStats(); loadRules(); loadConduits(); }
        else setActionError(res.data.error || "Failed to delete zone");
      })
      .catch((err) => {
        console.error("[handleDeleteZone] Error:", err);
        setActionError(err?.response?.data?.error || "Failed to delete zone");
      });
  };

  // Azione esplicita (non automatica ad ogni save): il firmware scrive
  // orion_overrides.zeek e fa il deploy di Zeek, che non è hot-reload --
  // meglio farlo scegliere all'utente quando davvero vuole sincronizzare,
  // non ad ogni piccola modifica di una zona/regola.
  const handlePushToOrion = () => {
    setPushState({ status: "pushing", message: "" });
    API.pushOrionConfig()
      .then((res) => {
        if (res.data.success) {
          const assigned = res.data.assignedCount || 0;
          setPushState({
            status: "done",
            message: "Configuration applied on the sensor." + (assigned > 0
              ? ` ${assigned} previously-unassigned asset${assigned === 1 ? "" : "s"} matched a zone's subnet/VLAN and now show it in Inventory.`
              : ""),
          });
          loadZoneStats(); // refresh pushMeta.pendingPush now that the push succeeded
        } else {
          setPushState({ status: "error", message: res.data.error || "The sensor rejected the configuration." });
        }
      })
      .catch((err) => {
        const message = err.response?.data?.error || err.message || "Failed to reach the sensor.";
        setPushState({ status: "error", message });
      });
  };

  const zoneNames = useMemo(() => Array.from(new Set(zoneStats.map((z) => z.zone))).sort(), [zoneStats]);

  // Indice di livello per ogni zona (per nome) -- serve a decidere se un
  // conduit è "locale" (stesso livello o adiacente) o "flyover" (salta
  // almeno un livello), quindi come instradarlo.
  const tierIndexByZone = useMemo(() => {
    const map = {};
    zoneStats.forEach((z) => {
      const lvl = z.purdue_level;
      map[z.zone] = (lvl === null || lvl === undefined)
        ? UNCLASSIFIED_INDEX
        : TIERS.findIndex((t) => t.level === Number(lvl));
    });
    return map;
  }, [zoneStats]);

  const registerCard = useCallback((zoneName) => (el) => {
    if (el) cardNodes.current[zoneName] = el;
    else delete cardNodes.current[zoneName];
  }, []);

  // Le card sono DOM reale, i conduit sono un overlay SVG disegnato
  // misurando le posizioni delle card dopo il render/resize. I conduit
  // "flyover" (che saltano almeno un livello) sono instradati in un
  // corridoio riservato sul lato destro del canvas -- riservato dal LAYOUT
  // (ogni corsia lascia GUTTER px liberi alla propria destra, vedi il Box
  // spacer nelle righe più sotto), non da una stima a posteriori: per
  // questo non possono mai finire sopra una card, indipendentemente da
  // quanto sia larga la finestra.
  const recomputeEdges = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const containerRect = container.getBoundingClientRect();
    setSize({ width: containerRect.width, height: containerRect.height });

    // Un conduit "grezzo" per record (zona, zona, servizio) può far
    // apparire più linee sovrapposte tra la stessa coppia di zone -- si
    // aggrega qui in UN solo edge per coppia (chiave non ordinata), con un
    // contatore e i record originali conservati in `members` per
    // l'Inspector/tooltip. La direzione visiva (freccia) resta quella del
    // primo membro incontrato, puramente per l'orientamento della curva.
    const groups = new Map();
    (conduits || []).forEach((c) => {
      if (!c.zone_orig || !c.zone_resp || c.zone_orig === c.zone_resp) return;
      const pairKey = [c.zone_orig, c.zone_resp].sort().join("|");
      if (!groups.has(pairKey)) groups.set(pairKey, []);
      groups.get(pairKey).push(c);
    });

    const raw = [];
    groups.forEach((members, pairKey) => {
      const first = members[0];
      const a = cardNodes.current[first.zone_orig];
      const b = cardNodes.current[first.zone_resp];
      if (!a || !b) return;
      const ra = a.getBoundingClientRect();
      const rb = b.getBoundingClientRect();
      const tierGap = Math.abs((tierIndexByZone[first.zone_orig] ?? 0) - (tierIndexByZone[first.zone_resp] ?? 0));
      // "Non permesso" o "solo dichiarato" (mai osservato) su un qualsiasi
      // membro basta a marcare l'intero edge aggregato, per non nascondere
      // un problema dietro un conduit "buono" nello stesso fascio -- TRANNE
      // quando è un mix di regole direzionali opposte legittime (una allowed
      // A→B, una not-allowed B→A, vedi ConduitRuleManagerDialog): quello è
      // "mixed", non "tutto bloccato" (vedi MIXED_COLOR).
      const allowed = members.every((m) => !!m.allowed);
      const mixed = !allowed && members.some((m) => !!m.allowed);
      const declared = members.every((m) => m.source === "declared");
      const lastSeen = members.reduce((max, m) => (m.last_seen && (!max || m.last_seen > max) ? m.last_seen : max), null);
      const memberLabel = (m) => {
        const dir = m.source === "declared" && m.direction && m.direction !== "both"
          ? ` (${m.direction === "a_to_b" ? `${m.zone_orig} → ${m.zone_resp}` : `${m.zone_resp} → ${m.zone_orig}`})`
          : "";
        return `${m.service || "any"}${dir} · ${m.allowed ? "allowed" : "NOT allowed"}${m.source === "declared" ? " · declared, not yet observed" : ""}`;
      };
      const title = `${first.zone_orig} ⇄ ${first.zone_resp} · ${members.length} conduit${members.length === 1 ? "" : "s"}\n` +
        members.map(memberLabel).join("\n") +
        (lastSeen ? `\nlast seen ${dayjs(lastSeen).format("YYYY-MM-DD HH:mm:ss")}` : "");
      raw.push({
        zone_orig: first.zone_orig,
        zone_resp: first.zone_resp,
        key: pairKey,
        pairKey,
        members,
        count: members.length,
        x1: ra.left + ra.width / 2 - containerRect.left,
        y1: ra.top + ra.height / 2 - containerRect.top,
        x2: rb.left + rb.width / 2 - containerRect.left,
        y2: rb.top + rb.height / 2 - containerRect.top,
        cardHeight: Math.max(ra.height, rb.height),
        tierGap, declared, allowed, mixed, title,
      });
    });

    // "Locale" in senso stretto = vicini di colonna nello stesso livello o
    // in un livello adiacente, cioè una linea verticale diretta molto corta
    // che non deve scavalcare nessun'altra card.
    const isDirectVertical = (e) => e.tierGap <= 1 && Math.abs(e.x1 - e.x2) < 4;
    // Due zone dello STESSO livello (fianco a fianco, non in verticale):
    // segnalato dall'utente come rotto -- bowX verso il corridoio a destra
    // NON funziona qui, perché la curva resta comunque alla stessa altezza
    // (y) della riga per tutta la sua lunghezza (tierGap=0 ⇒ y1≈y2), quindi
    // continua a tagliare in mezzo a qualsiasi card si trovi tra le due,
    // indipendentemente da quanto bowX sia spostato a destra. L'unico modo
    // per non attraversare la riga è uscirne in VERTICALE (in alto, nello
    // spazio vuoto tra questa corsia e quella sopra), non spostarsi in
    // orizzontale restando alla stessa quota.
    const isSameTier = (e) => e.tierGap === 0 && !isDirectVertical(e);

    // Un solo edge per coppia di zone ormai (aggregato sopra), quindi niente
    // più leggeri offset paralleli da calcolare: la curva passa dritta per
    // il punto medio.
    raw.filter(isDirectVertical).forEach((e) => {
      e.pathType = "quad";
      e.cx = (e.x1 + e.x2) / 2;
      e.cy = (e.y1 + e.y2) / 2;
      e.mx = 0.25 * e.x1 + 0.5 * e.cx + 0.25 * e.x2;
      e.my = 0.25 * e.y1 + 0.5 * e.cy + 0.25 * e.y2;
    });

    raw.filter(isSameTier).forEach((e) => {
      // Il punto di controllo di una quadratica sposta il PICCO della
      // curva solo a metà strada (per y1≈y2 il picco è a (y1+cy)/2, non a
      // cy) -- un bow di 55 sul punto di controllo alzava il picco reale
      // di soli ~27px, che restava dentro il bordo alto della card (altezza
      // reale ~90-110px a seconda del contenuto), quindi l'etichetta ci
      // finiva sopra/dietro e veniva "tagliata" dalla card opaca dipinta
      // dopo l'SVG. Serve il DOPPIO dello scostamento desiderato sul punto
      // di controllo, calcolato sull'altezza reale misurata della card
      // (non un numero fisso), con un margine di sicurezza.
      const clearance = e.cardHeight / 2 + 18; // sopra il bordo alto della card
      e.pathType = "quad";
      e.cx = (e.x1 + e.x2) / 2;
      e.cy = Math.min(e.y1, e.y2) - clearance * 2;
      e.mx = 0.25 * e.x1 + 0.5 * e.cx + 0.25 * e.x2;
      e.my = 0.25 * e.y1 + 0.5 * e.cy + 0.25 * e.y2;
    });

    // Tutto il resto (livello diverso, colonna diversa -- non un salto
    // verticale diretto): corridoio a destra, cubica con entrambi i punti
    // di controllo ancorati al corridoio riservato -- questo è ciò che
    // tiene la curva incollata al corridoio per quasi tutta la sua
    // lunghezza (a differenza di un singolo punto di controllo, che la
    // farebbe rientrare verso il centro proprio all'altezza delle corsie
    // intermedie, cioè esattamente dove ci sono le card da evitare). Qui
    // funziona perché y1 e y2 sono già su corsie diverse, quindi la curva
    // esce dalla propria corsia comunque, a differenza del caso stesso
    // livello sopra.
    let flyoverLane = 0;
    raw.filter((e) => !isDirectVertical(e) && !isSameTier(e)).forEach((e) => {
      flyoverLane += 1;
      const bowX = containerRect.width - GUTTER / 2 - Math.min((flyoverLane - 1) * 18, GUTTER / 2 - 15);
      e.pathType = "cubic";
      e.c1x = bowX; e.c1y = e.y1 + (e.y2 - e.y1) * 0.15;
      e.c2x = bowX; e.c2y = e.y2 - (e.y2 - e.y1) * 0.15;
      e.mx = 0.125 * e.x1 + 0.375 * e.c1x + 0.375 * e.c2x + 0.125 * e.x2;
      e.my = 0.125 * e.y1 + 0.375 * e.c1y + 0.375 * e.c2y + 0.125 * e.y2;
    });

    setEdges(raw);
  }, [conduits, tierIndexByZone]);

  useLayoutEffect(() => { recomputeEdges(); }, [recomputeEdges, zoneStats, selected]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => recomputeEdges());
    observer.observe(container);
    return () => observer.disconnect();
  }, [recomputeEdges]);

  // zoneStatsWithScore is already sorted riskiest-first, and .push() preserves
  // that order within each tier's array, so each row lists its worst zone first.
  const zonesByLevel = new Map();
  const unclassified = [];
  zoneStatsWithScore.forEach((z) => {
    if (z.purdue_level === null || z.purdue_level === undefined) {
      unclassified.push(z);
      return;
    }
    const key = Number(z.purdue_level);
    if (!zonesByLevel.has(key)) zonesByLevel.set(key, []);
    zonesByLevel.get(key).push(z);
  });

  const selectedZoneObj = selected ? zoneStatsWithScore.find((z) => z.zone === selected) : null;
  const relatedKeys = selected
    ? new Set(edges.filter((e) => e.zone_orig === selected || e.zone_resp === selected).map((e) => e.pairKey))
    : null;

  const handleSelect = (zoneName) => {
    setSelectedPair(null);
    setSelected((cur) => (cur === zoneName ? null : zoneName));
  };

  // Matrix view: click su intestazione riga/colonna -> stesso Inspector
  // della topology view (`selected`); click su una CELLA -> pannello
  // dedicato alla coppia (`selectedPair`). Mutuamente esclusivi.
  const handleSelectZoneFromMatrix = (zoneName) => {
    setSelectedPair(null);
    setSelected((cur) => (cur === zoneName ? null : zoneName));
  };
  const handleSelectPair = (zoneA, zoneB) => {
    setSelected(null);
    setSelectedPair((cur) => (cur && cur.zoneA === zoneA && cur.zoneB === zoneB ? null : { zoneA, zoneB }));
  };

  const openCreateZone = (purdueLevel) => setFormDialog({ open: true, zone: null, initialPurdueLevel: purdueLevel });
  const openEditZone = (zone) => setFormDialog({ open: true, zone, initialPurdueLevel: null });
  const closeZoneForm = () => setFormDialog({ open: false, zone: null, initialPurdueLevel: null });

  // Da Inspector/Matrix, dove si ha solo il NOME della zona (vista
  // arricchita zoneStats) -- risolve il documento Zone grezzo per l'edit.
  // Se non esiste ancora (zona vista solo da Orion, mai definita
  // manualmente) apre comunque il form con il nome precompilato: Save la
  // "promuove" a zona gestita invece di fallire silenziosamente.
  const openEditZoneByName = (zoneName) => {
    const raw = manualZones.find((z) => z.name === zoneName);
    openEditZone(raw || { name: zoneName });
  };
  const handleDeleteZoneByName = (zoneName) => {
    const raw = manualZones.find((z) => z.name === zoneName);
    if (!raw) return; // niente da eliminare, la zona non è (ancora) gestita
    setDeleteZoneDialog({ open: true, zone: raw });
  };

  const renderRow = (levelKey, label, Icon, zones, dashedBorder) => (
    <Box key={levelKey} sx={{ display: "flex", minHeight: 100 }}>
      <Box sx={{ width: 150, flexShrink: 0, display: "flex", alignItems: "center", gap: 1, pr: 1.2 }}>
        <Box sx={{ width: 24, height: 24, borderRadius: 1, bgcolor: "#1c1c1c", border: `1px solid ${colors.grey[800]}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Icon sx={{ fontSize: 14, color: colors.grey[500] }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: colors.grey[600], letterSpacing: 0.4, textTransform: "uppercase" }}>
            {levelKey === "u" ? "Unclassified" : `Level ${levelKey}`}
          </Typography>
          <Typography sx={{ fontSize: 10.5, fontWeight: 600, color: colors.grey[400] }}>{label}</Typography>
        </Box>
        <Tooltip title={`Add a zone on ${levelKey === "u" ? "no Purdue level" : `Level ${levelKey}`}`}>
          <IconButton
            size="small"
            onClick={() => openCreateZone(levelKey === "u" ? null : levelKey)}
            sx={{
              ml: "auto", p: 0.5, flexShrink: 0,
              color: "#459095", bgcolor: "#45909522", border: "1px solid #45909555",
              "&:hover": { color: "#fff", bgcolor: "#459095" },
            }}
          >
            <AddIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>
      <Box sx={{
        flex: 1, display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap",
        borderTop: `1px dashed ${colors.grey[850] || "#1f1f1f"}`, py: 1.2,
      }}>
        {zones.length === 0 ? (
          <Typography sx={{ fontSize: 11.5, color: colors.grey[700], fontStyle: "italic" }}>—</Typography>
        ) : (
          zones.map((z) => (
            <ZoneNode
              key={z.zone}
              zone={{ ...z, tierIcon: Icon }}
              cardRef={registerCard(z.zone)}
              selected={selected === z.zone}
              dimmed={!!selected && selected !== z.zone && !relatedKeys?.has(`${selected}|${z.zone}`) && !relatedKeys?.has(`${z.zone}|${selected}`)}
              onClick={handleSelect}
            />
          ))
        )}
      </Box>
      {/* Spacer riservato: stessa larghezza su ogni riga, forma il
          corridoio verticale continuo dove i conduit "flyover" vengono
          instradati -- nessuna card può mai occuparlo. */}
      <Box sx={{ width: GUTTER, flexShrink: 0 }} />
    </Box>
  );

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, mb: 1 }}>
        <Typography sx={{ fontSize: 11.5, color: colors.grey[500], fontStyle: "italic", flex: 1 }}>
          Risk is the manually-assessed zone risk (same as the Zones feature elsewhere in the app). Security Level only
          appears when Orion has visibility on that zone — achieved SL is a heuristic proxy from passive traffic evidence,
          a triage input, not a formal IEC 62443-3-3 assessment. Click a zone for detail.
        </Typography>
        <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
          <ToggleButtonGroup
            size="small" exclusive value={view}
            onChange={(e, v) => v && setView(v)}
            sx={{
              "& .MuiToggleButton-root": {
                textTransform: "none", fontSize: 12, px: 1.6, color: colors.grey[400], borderColor: colors.grey[800],
              },
              "& .MuiToggleButton-root.Mui-selected": { color: "#459095", bgcolor: "#45909522" },
            }}
          >
            <ToggleButton value="topology">Topology</ToggleButton>
            <ToggleButton value="matrix">Matrix</ToggleButton>
          </ToggleButtonGroup>
          <Button
            size="small" variant="outlined"
            startIcon={<RuleIcon sx={{ fontSize: "16px !important" }} />}
            onClick={() => setRulesDialogOpen(true)}
            sx={{ textTransform: "none", fontSize: 12.5, flexShrink: 0 }}
          >
            Manage conduit rules
          </Button>
          <Button
            size="small" variant="outlined" color="secondary"
            startIcon={<CloudUploadIcon sx={{ fontSize: "16px !important" }} />}
            onClick={handlePushToOrion}
            disabled={pushState.status === "pushing"}
            sx={{ textTransform: "none", fontSize: 12.5, flexShrink: 0 }}
          >
            {pushState.status === "pushing" ? "Pushing…" : "Push to Orion"}
          </Button>
        </Box>
      </Box>

      {actionError && (
        <Box sx={{
          mb: 1.5, px: 1.2, py: 0.8, borderRadius: 1, fontSize: 12,
          bgcolor: "#ff44441c", color: DENIED_COLOR, border: `1px solid ${DENIED_COLOR}`,
          display: "flex", alignItems: "center", gap: 0.8,
        }}>
          <WarningAmberIcon sx={{ fontSize: 15, flexShrink: 0 }} />
          {actionError}
          <Button size="small" onClick={() => setActionError("")} sx={{ ml: "auto", minWidth: "auto", color: DENIED_COLOR, fontSize: 11, textTransform: "none" }}>
            Dismiss
          </Button>
        </Box>
      )}

      {pushState.status === "idle" && pushMeta.pendingPush && (
        <Box sx={{
          mb: 1.5, px: 1.2, py: 0.8, borderRadius: 1, fontSize: 12,
          bgcolor: "#e0b23a1c", color: "#e0b23a", border: "1px solid #e0b23a",
          display: "flex", alignItems: "center", gap: 0.8,
        }}>
          <WarningAmberIcon sx={{ fontSize: 15, flexShrink: 0 }} />
          Zones or conduit rules changed since the last push — Orion is still running the previous configuration until you push again.
        </Box>
      )}

      {pushState.status !== "idle" && pushState.status !== "pushing" && (
        <Box sx={{
          mb: 1.5, px: 1.2, py: 0.8, borderRadius: 1, fontSize: 12,
          bgcolor: pushState.status === "done" ? "#4cceac1c" : "#ff44441c",
          color: pushState.status === "done" ? ALLOWED_COLOR : DENIED_COLOR,
          border: `1px solid ${pushState.status === "done" ? ALLOWED_COLOR : DENIED_COLOR}`,
        }}>
          {pushState.message}
        </Box>
      )}

      <Box sx={{ display: "flex", gap: 2.5, flexWrap: "wrap", mb: 1.5, px: 0.5 }}>
        {[
          { color: ALLOWED_COLOR, dash: undefined, label: "Observed · allowed" },
          { color: DENIED_COLOR, dash: "6,3", label: "Observed · NOT allowed" },
          { color: ALLOWED_COLOR, dash: "2,3", label: "Declared rule · allowed" },
          { color: DENIED_COLOR, dash: "2,3", label: "Declared rule · NOT allowed" },
          { color: MIXED_COLOR, dash: "2,3", label: "Mixed · one-way allowed, other way not" },
        ].map(({ color, dash, label }) => (
          <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
            <svg width="26" height="8"><line x1="1" y1="4" x2="25" y2="4" stroke={color} strokeWidth={dash ? 1.6 : 2} strokeDasharray={dash} /></svg>
            <Typography sx={{ fontSize: 11, color: colors.grey[400] }}>{label}</Typography>
          </Box>
        ))}
      </Box>

      <ConduitRuleManagerDialog
        open={rulesDialogOpen}
        onClose={() => setRulesDialogOpen(false)}
        zoneNames={zoneNames}
        rules={rules}
        onCreateRule={handleCreateRule}
        onUpdateRule={handleUpdateRule}
        onDeleteRule={handleDeleteRule}
      />

      {/* Create/edit di una zona alla volta -- "+" per riga/livello (create,
          purdue_level precompilato) o pannello Inspector di una zona
          selezionata (edit/delete). L'assegnazione massiva agli asset resta
          dove è sempre stata (Overview/Inventory). */}
      <ZoneFormDialog
        open={formDialog.open}
        onClose={closeZoneForm}
        zone={formDialog.zone}
        initialPurdueLevel={formDialog.initialPurdueLevel}
        zones={manualZones}
        onCreate={handleCreateZone}
        onUpdate={handleUpdateZone}
        onDelete={handleDeleteZone}
        conduitRules={rules}
        onCreateRule={handleCreateRule}
        onUpdateRule={handleUpdateRule}
        onDeleteRule={handleDeleteRule}
      />

      <WarningDialog
        open={deleteZoneDialog.open}
        setOpen={(v) => setDeleteZoneDialog((prev) => ({ ...prev, open: v }))}
        setCheck={() => {
          const raw = deleteZoneDialog.zone;
          setDeleteZoneDialog({ open: false, zone: null });
          if (!raw) return;
          handleDeleteZone(raw._id ?? raw.id);
          setSelected(null);
        }}
        message={`Zone "${deleteZoneDialog.zone?.name ?? ""}" will be permanently deleted. This cannot be undone.`}
      />

      {view === "matrix" ? (
        <Box sx={{ display: "flex", border: `1px solid ${colors.grey[800]}`, borderRadius: 1.5, bgcolor: "#151515" }}>
          <Box sx={{ flex: 1, minWidth: 0, p: 2.5, overflowX: "auto" }}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1.5 }}>
              <Tooltip title="Add a zone">
                <IconButton
                  size="small"
                  onClick={() => openCreateZone(null)}
                  sx={{
                    p: 0.6, color: "#459095", bgcolor: "#45909522", border: "1px solid #45909555",
                    "&:hover": { color: "#fff", bgcolor: "#459095" },
                  }}
                >
                  <AddIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            </Box>
            <MatrixView
              zoneStats={zoneStats}
              conduits={conduits}
              onSelectZone={handleSelectZoneFromMatrix}
              onSelectPair={handleSelectPair}
            />
          </Box>
          <Box sx={{ width: 260, flexShrink: 0, borderLeft: `1px solid ${colors.grey[800]}` }}>
            {selectedPair ? (
              <PairInspector pair={selectedPair} conduits={conduits} onOpenRules={() => setRulesDialogOpen(true)} />
            ) : (
              <Inspector zone={selectedZoneObj} edges={edges} onEdit={openEditZoneByName} onDelete={handleDeleteZoneByName} />
            )}
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: "flex", border: `1px solid ${colors.grey[800]}`, borderRadius: 1.5, bgcolor: "#151515" }}>
          {/* niente overflow:hidden qui -- un arco/etichetta che sale sopra
              la prima corsia (nessuna riga sopra da evitare, quindi via
              libera) andrebbe tagliato dal contenitore stesso invece che
              rientrare, prima ancora che l'SVG possa disegnarlo. */}
          <Box ref={containerRef} sx={{ flex: 1, position: "relative", p: 2 }}>
            <svg
              width={size.width} height={size.height}
              style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none", overflow: "visible" }}
            >
              <defs>
                <marker id="nz-arrow-allowed" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                  <path d="M0,0 L6,3 L0,6 Z" fill={ALLOWED_COLOR} />
                </marker>
                <marker id="nz-arrow-denied" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                  <path d="M0,0 L6,3 L0,6 Z" fill={DENIED_COLOR} />
                </marker>
                <marker id="nz-arrow-mixed" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                  <path d="M0,0 L6,3 L0,6 Z" fill={MIXED_COLOR} />
                </marker>
              </defs>
              {edges.map((e) => {
                const color = e.mixed ? MIXED_COLOR : e.allowed ? ALLOWED_COLOR : DENIED_COLOR;
                const marker = e.mixed ? "mixed" : e.allowed ? "allowed" : "denied";
                const involved = !selected || e.zone_orig === selected || e.zone_resp === selected;
                const opacity = involved ? (e.declared ? 0.7 : 0.9) : 0.1;
                const d = e.pathType === "cubic"
                  ? `M ${e.x1},${e.y1} C ${e.c1x},${e.c1y} ${e.c2x},${e.c2y} ${e.x2},${e.y2}`
                  : `M ${e.x1},${e.y1} Q ${e.cx},${e.cy} ${e.x2},${e.y2}`;
                return (
                  <g key={e.key}>
                    <path
                      d={d} fill="none" stroke={color}
                      strokeWidth={e.declared ? 1.4 : e.allowed ? 1.6 : 2.2}
                      strokeDasharray={e.declared ? "2,3" : e.allowed ? undefined : "6,3"}
                      opacity={opacity}
                      markerEnd={`url(#nz-arrow-${marker})`}
                    >
                      <title>{e.title}</title>
                    </path>
                    {e.count > 1 && involved && (
                      <>
                        <circle cx={e.mx} cy={e.my} r={8} fill="#151515" stroke={color} strokeWidth={1} opacity={opacity} />
                        <text x={e.mx} y={e.my + 3} fontSize={9.5} fontWeight={700} fill={color} textAnchor="middle" opacity={opacity}>
                          {e.count}
                        </text>
                      </>
                    )}
                  </g>
                );
              })}
            </svg>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, position: "relative" }}>
              {TIERS.map((t) => renderRow(t.level, t.label, t.Icon, zonesByLevel.get(t.level) || []))}
              {unclassified.length > 0 && renderRow("u", "No Purdue level", WifiIcon, unclassified)}
            </Box>
          </Box>

          <Box sx={{ width: 260, flexShrink: 0, borderLeft: `1px solid ${colors.grey[800]}` }}>
            <Inspector zone={selectedZoneObj} edges={edges} onEdit={openEditZoneByName} onDelete={handleDeleteZoneByName} />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default NetworkZones;
